import { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import '../styles/pages/Contact.css';
import useReveal from '../components/useReveal';
import PRODUCTS from '../data/products';

// ─── Replace these three values with your EmailJS credentials ───
const EMAILJS_SERVICE_ID  = 'service_29yrzaq';
const EMAILJS_TEMPLATE_ID = 'template_a9fizmw';
const EMAILJS_PUBLIC_KEY  = 'gm0XBa8oxmrD5Cjs_';
// ────────────────────────────────────────────────────────────────

const CONTACT_INFO = [
  { icon: '🏢', title: 'Corporate Office',      value: 'Unit No. 209, Tower-D, Pioneer Urban Square, Sector 62, Gurugram, India' },
  { icon: '🏭', title: 'Manufacturing Facility', value: '710-711, MIE, Bahadurgarh, Haryana — 124507, India' },
  { icon: '📧', title: 'Export Enquiries',        value: 'export@phrmchem.net',     href: 'mailto:export@phrmchem.net' },
  { icon: '📧', title: 'Domestic Sales',          value: 'sales@phrmchem.net',      href: 'mailto:sales@phrmchem.net' },
  { icon: '👤', title: 'Management',              value: 'nipun@phrmchem.net',      href: 'mailto:nipun@phrmchem.net' },
  { icon: '📱', title: 'Mobile',                  value: '+91 98108 20562',          href: 'tel:+919810820562' },
  { icon: '🌐', title: 'Website',                 value: 'www.pharmchem.net.in',     href: 'http://www.pharmchem.net.in' },
];

const COUNTRIES = [
  'India','United Arab Emirates','Bangladesh','Argentina','Bolivia','Cambodia',
  'Chile','Colombia','Egypt','Ethiopia','Germany','Ghana','Guatemala',
  'Indonesia','Kenya','Mexico','Nepal','Nigeria','Pakistan','Peru',
  'Sudan','Switzerland','Tanzania','Thailand','Other',
];

function Contact() {
  useReveal();

  const formRef = useRef();
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'sent' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async () => {
    const form = formRef.current;

    // Basic validation
    const required = form.querySelectorAll('[required]');
    for (const field of required) {
      if (!field.value.trim()) {
        field.focus();
        setStatus('error');
        setErrorMsg('Please fill in all required fields.');
        return;
      }
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        form,
        EMAILJS_PUBLIC_KEY
      );
      setStatus('sent');
      form.reset();
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or email us directly.');
    }
  };

  return (
    <>
      {/* Page hero */}
      <div className="page-hero">
        <div className="page-hero-bg"></div>
        <div className="page-hero-inner">
          <div className="eyebrow">Get In Touch</div>
          <h1 className="headline">
            Let's Build Something <em>Together</em>
          </h1>
          <p>
            Whether you're looking for a new API supplier or want to expand your existing
            partnership with Pharmchem, our team is ready to assist.
          </p>
        </div>
      </div>

      {/* Contact section */}
      <div className="contact-page">
        <div className="contact-inner">

          {/* Left — info card */}
          <div>
            <div className="contact-info-block">
              <div className="cib-header">
                <h3>Pharmchem</h3>
                <p>Building Business through Long Term Relations</p>
              </div>
              <div className="cib-body">
                {CONTACT_INFO.map((ci) => (
                  <div className="ci-row" key={ci.title}>
                    <div className="ci-icon">{ci.icon}</div>
                    <div className="ci-text">
                      <strong>{ci.title}</strong>
                      {ci.href
                        ? <a href={ci.href} target={ci.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{ci.value}</a>
                        : ci.value
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — enquiry form */}
          <div className="contact-form-wrap">
            <h3>Send an Enquiry</h3>
            <p>Fill in the form below and our team will get back to you within 24 hours.</p>

            {/* All inputs inside this ref'd div so emailjs.sendForm() can read them */}
            <form className="form-grid" ref={formRef} onSubmit={(e) => e.preventDefault()}>
              

              {/* Name row */}
              <div className="form-group">
                <label>First Name <span className="required">*</span></label>
                <input type="text" name="first_name" placeholder="John" required />
              </div>
              <div className="form-group">
                <label>Last Name <span className="required">*</span></label>
                <input type="text" name="last_name" placeholder="Doe" required />
              </div>

              {/* Contact row */}
              <div className="form-group">
                <label>Email Address <span className="required">*</span></label>
                <input type="email" name="email" placeholder="john@company.com" required />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" name="phone" placeholder="+1 234 567 8900" />
              </div>

              {/* Company */}
              <div className="form-group form-full">
                <label>Company Name <span className="required">*</span></label>
                <input type="text" name="company" placeholder="Your pharmaceutical company" required />
              </div>

              {/* Country */}
              <div className="form-group form-full">
                <label>Country <span className="required">*</span></label>
                <select name="country" defaultValue="" required>
                  <option value="" disabled>Select your country…</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Product */}
              <div className="form-group form-full">
                <label>Product(s) of Interest</label>
                <select name="product" defaultValue="">
                  <option value="" disabled>Select a product…</option>
                  {PRODUCTS.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                  <option value="Multiple Products">Multiple Products</option>
                  <option value="General Enquiry">General Enquiry</option>
                </select>
              </div>

              {/* Message */}
              <div className="form-group form-full">
                <label>Message / Requirements <span className="required">*</span></label>
                <textarea
                  name="message"
                  placeholder="Please describe your requirements — quantity, specifications, delivery timeline, or any other details…"
                  required
                ></textarea>
              </div>

              {/* Status messages */}
              {status === 'sent' && (
                <div className="form-full form-success">
                  ✓ Enquiry sent! We'll get back to you within 24 hours.
                </div>
              )}
              {status === 'error' && (
                <div className="form-full form-error">
                  ⚠ {errorMsg}
                </div>
              )}

              {/* Submit */}
              <div className="form-full">
                <button
                  className={`form-submit ${status === 'sent' ? 'sent' : ''}`}
                  onClick={handleSubmit}
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? 'Sending…'
                    : status === 'sent'  ? '✓ Enquiry Sent!'
                    : 'Send Enquiry →'}
                </button>
              </div>
            </form>

          </div>

        </div>
      </div>
    </>
  );
}

export default Contact;